import { Notification, toast } from "components/ui";
import { SessionContext } from "./SessionContext";
import React, { useContext } from "react";
import { CompanyContext } from "./CompanyContext";
import { api as Api } from "api/http";
import { useSelector } from "react-redux";
import { AppSumoContext } from "./AppSumoContext";
import { stripMarkdown } from "utils/stripMarkdown";
import { cleanAIText } from "utils/cleanAIText";
import { pollAiResult } from "utils/pollAiResult";
import { CompanyOnboardingContext } from "./CompanyOnboardingContext";
import openaiImg from "../assets/images/drivers/openai.png";
import claudeImg from "../assets/images/drivers/claude.png";
import stabilityImg from "../assets/images/drivers/stability.png";
import bytedanceImg from "../assets/images/drivers/bytedance.png";
import ideogramImg from "../assets/images/drivers/ideogram.png";
// Gemini disabled — see security incident 2026-04.
// import geminiImg from "../assets/images/drivers/gemini.png";
import falImg from "../assets/images/drivers/fal.png";
import creatomateImg from "../assets/images/drivers/creatomate.png";
import recraftImg from "../assets/images/drivers/recraft.png";
import deepseekImg from "../assets/images/drivers/deepseek.png";
import googleImg from "../assets/images/drivers/google.png";
import hailuoImg from "../assets/images/drivers/hailuo.png";
import wanImg from "../assets/images/drivers/wan.png";
import seedanceImg from "../assets/images/drivers/seedance.png";
import soraImg from "../assets/images/drivers/sora.png";
import beatovenImg from "../assets/images/drivers/beatoven.png";
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';

import elevenLabsImg from "../assets/images/models/elevenlabs.png";
import veedImg from "../assets/images/models/veed.png";

export const aiSizeOptions = [
  { 
    id: "1:1",
    aspect_ratio: "1:1", 
    label: "1:1",
    category: "square"
  },
  { 
    id: "4:3",
    aspect_ratio: "4:3", 
    label: "4:3",
    category: "landscape"
  },
  { 
    id: "3:2",
    aspect_ratio: "3:2", 
    label: "3:2",
    category: "landscape"
  },
  { 
    id: "16:9",
    aspect_ratio: "16:9", 
    label: "16:9",
    category: "landscape"
  },
  { 
    id: "3:4",
    aspect_ratio: "3:4", 
    label: "3:4",
    category: "portrait"
  },
  { 
    id: "2:3",
    aspect_ratio: "2:3", 
    label: "2:3",
    category: "portrait"
  },
  { 
    id: "9:16",
    aspect_ratio: "9:16", 
    label: "9:16",
    category: "portrait"
  },
];

const AIContext = React.createContext({
  getDriverModel: (type) => void 0,
  getImages: async (query = "", size = "1:1", _model = undefined, addColors = false, imageUrls = [], queue = false) => void 0,
  getVideos: async (query = "", size = "16:9", _model = undefined, imageUrl = undefined, enablePolling = true, audioUrl = undefined, duration = undefined, render_script = undefined) => void 0,
  getAudio: async (text = "", voice = "Rachel", _model = undefined, config = {}, queue = false) => void 0,
  getByQueryChat: async (query = "", isChargable = true, forceNewQuerySystem = false, newQuerySystem = "", aiResultId = undefined, config = {}, groupId = undefined, _model = undefined, jsonSchema = undefined) => void 0, 
  getCopy: async (task = "post", payload = {}, options = {}) => void 0,
  ensureOnboarded: () => void 0,
  getLastHistoryAIImageURL: async () => void 0,
  aiModels: [],
  availableAiModels: [],
  aiTextModels: [],
  aiImageModels: [],
  aiVideoModels: [],
  aiAudioModels: [],
  creativeStudioImageModel: 'nano_banana_2_1',
  systemMessage: ""
});

// The danger toast shown when a text generation request fails. Copy generation fires several
// requests in parallel, so it shows this once for the whole batch instead of once per request.
const showTextGenerationError = (err, chargeable = true) => {
  toast.push(
    <Notification
      width={400}
      closable={true}
      duration={10000}
      title={
        <div className="flex flex-col gap-2">
          <b>AI Text Generation Error:</b>
          <p><i>{err?.message || err?.msg || err}</i></p>
          {chargeable && <p>Word credits have not been deducted from your account.</p>}
        </div>
      }
      type="danger"
    />
  );
}

// Structured responses are JSON strings; tolerate stray ```json fences around them.
const parseJSONResponse = (raw = "") => {
  const unfenced = raw
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();
  return JSON.parse(unfenced);
}

// Retired model ids and their successor, mirroring app/Services/Ai/ModelAliases.php in the
// API, so saved company preferences keep resolving to a model that exists.
export const legacyModelAliases = {
  'deepseek-chat': 'deepseek-v4-flash',
  'deepseek-reasoner': 'deepseek-v4-pro',
  'gpt-3.5-turbo-0125': 'gpt-6-luna',
  'gpt-4o-mini': 'gpt-6-luna',
  'gpt-4.1-mini': 'gpt-6-luna',
  'gpt-4.1-nano': 'gpt-6-luna',
  'gpt-5-nano': 'gpt-6-luna',
  'gpt-4o': 'gpt-6.1-sol',
  'gpt-4.1': 'gpt-6.1-sol',
  'o1-mini': 'gpt-6.1-sol',
  'o1-preview': 'gpt-6.1-sol',
  'gpt-5': 'gpt-6.1-sol',
  'gpt-5-mini': 'gpt-6.1-sol',
  'claude-3-haiku-20240307': 'claude-haiku-5-5',
  'claude-3-5-haiku-20241022': 'claude-haiku-5-5',
  'claude-haiku-4-5-20251001': 'claude-haiku-5-5',
  'claude-3-5-sonnet-20240620': 'claude-sonnet-5-5',
  'claude-3-7-sonnet-20250219': 'claude-sonnet-5-5',
  'claude-sonnet-4-20250514': 'claude-sonnet-5-5',
  'claude-sonnet-4-5-20250929': 'claude-sonnet-5-5',
  'claude-opus-4-20250514': 'claude-sonnet-5-5',
  'flux_schnell': 'flux_3_klein',
  'flux_dev': 'flux_3_klein',
  'flux_pro': 'flux_3',
  'flux_pro_1.1': 'flux_3',
  'flux_pro_1.1_ultra': 'flux_3',
  'flux_realism': 'flux_3',
  'flux_pro_kontext': 'flux_3',
  'imagen4_preview': 'nano_banana_2_1',
  'imagen4_preview_fast': 'nano_banana_2_1',
  'nano_banana': 'nano_banana_2_1',
  'nano_banana_2': 'nano_banana_2_1',
  'seedream_v4': 'seedream_v5_lite',
  'wan_25_preview': 'seedream_v5_lite',
  'ideogram_v3': 'ideogram_v4_5',
  'gpt_image_2': 'gpt_image_2_5',
  'gpt-image-1-auto': 'gpt_image_2_5',
  'veo_3': 'veo_3.1',
  'sora_2_pro': 'veo_3.1',
  'veo_3_fast': 'veo_3.1_fast',
  'sora_2': 'veo_3.1_fast',
  'seedance_1.1_light': 'h3_max_turbo',
  'hailuo_02_standard': 'h3_max_turbo',
  'hailuo_02_premium': 'h3_max_turbo',
  'seedance_1.1_pro': 'kling_3_turbo',
};

const AIProvider = ({ children }) => {
  
  const { updateBalance } = useContext(SessionContext);
  const { company } = useContext(CompanyContext);
  const { isSumoling, hasPremiumImages, hasPremiumText } = useContext(AppSumoContext);
  const { open: openCompanyOnboarding, isCompleted: companyOnboardingCompleted } = useContext(CompanyOnboardingContext);
  const locale = useSelector((state) => state.locale.currentLang);
  const { whitelabel } = useWhitelabel();

  const hasDriverActiveExternalApiKey = (driver) => {
    return !!company?.ai_keys?.find(e => e?.driver == driver)?.value && !!company?.ai_keys?.find(e => e?.driver == driver)?.active
  }
  
  const aiModels = [
    // {
    //   type: 'text',
    //   driver: 'openai',
    //   driverDisplayName: 'OpenAI',
    //   image: openaiImg,
    //   model: 'gpt-4o-mini',
    //   name: 'GPT-4o mini',
    //   fullName: 'OpenAI GPT-4o mini',
    //   price: 1,
    // },
    // Retired 2026-10-17 (shut down or superseded at the provider): gpt-4.1-mini, gpt-4.1,
    // gpt-4o, gpt-5-mini, o1-mini, claude-sonnet-4-5. Saved preferences are mapped to their
    // successor by legacyModelAliases in getDriverModel(), as the API does.
    // Prices are word credits per output word: Luna and Haiku 5.5 cost $0.10/$0.50 per MTok,
    // Sol and Sonnet 5.5 $2/$10. Thinking is off or at its lowest on all four.
    {
      type: 'text',
      driver: 'openai',
      driverDisplayName: 'OpenAI',
      image: openaiImg,
      model: 'gpt-6-luna',
      name: 'GPT-6 Luna',
      fullName: 'OpenAI GPT-6 Luna',
      price: 1,
      sysDefault: true,
      new: true
    },
    {
      type: 'text',
      driver: 'claude',
      driverDisplayName: 'Anthropic',
      image: claudeImg,
      model: 'claude-haiku-5-5',
      name: 'Claude Haiku 5.5',
      fullName: 'Claude Haiku 5.5',
      price: 1,
      new: true
    },
    {
      type: 'text',
      driver: 'claude',
      driverDisplayName: 'Anthropic',
      image: claudeImg,
      model: 'claude-sonnet-5-5',
      name: 'Claude Sonnet 5.5',
      fullName: 'Claude Sonnet 5.5',
      best: true,
      price: 10,
      disabled: isSumoling() && !hasPremiumText() && !hasDriverActiveExternalApiKey('claude'),
      new: true
    },
    {
      type: 'text',
      driver: 'openai',
      driverDisplayName: 'OpenAI',
      image: openaiImg,
      model: 'gpt-6.1-sol',
      name: 'GPT-6.1 Sol',
      fullName: 'OpenAI GPT-6.1 Sol',
      price: 10,
      disabled: isSumoling() && !hasPremiumText() && !hasDriverActiveExternalApiKey('openai'),
      new: true
    },
    {
      type: 'text',
      driver: 'deepseek',
      driverDisplayName: 'DeepSeek',
      image: deepseekImg,
      model: 'deepseek-v4-flash',
      name: 'DeepSeek V4 Flash',
      fullName: 'DeepSeek V4 Flash',
      price: 2,
      disabled: isSumoling() && !hasPremiumText() && !hasDriverActiveExternalApiKey('deepseek'),
    },
    // {
    //   type: 'text',
    //   driver: 'claude',
    //   driverDisplayName: 'Anthropic',
    //   image: claudeImg,
    //   model: 'claude-3-haiku-20240307',
    //   name: 'Calude 3',
    //   fullName: 'Claude 3 Haiku',
    //   price: 1,
    // },
    // {
    //   type: 'text',
    //   driver: 'claude',
    //   driverDisplayName: 'Anthropic',
    //   image: claudeImg,
    //   model: 'claude-3-5-haiku-20241022',
    //   name: 'Calude 3.5',
    //   fullName: 'Claude 3.5 Haiku',
    //   price: 5,
    //   disabled: isSumoling() && !hasPremiumText() && !hasDriverActiveExternalApiKey('claude'),
    // },
    // {
    //   type: 'text',
    //   driver: 'claude',
    //   driverDisplayName: 'Anthropic',
    //   image: claudeImg,
    //   model: 'claude-3-5-sonnet-20240620',
    //   name: 'Calude 3.5',
    //   fullName: 'Claude 3.5 Sonnet',
    //   price: 10,
    //   disabled: isSumoling() && !hasPremiumText() && !hasDriverActiveExternalApiKey('claude')
    // },
    // Gemini disabled — see security incident 2026-04.
    // {
    //   type: 'text',
    //   driver: 'gemini',
    //   driverDisplayName: 'Gemini',
    //   image: geminiImg,
    //   model: 'gemini-2.5-flash',
    //   name: 'Gemini 2.5 Flash',
    //   fullName: 'Gemini 2.5 Flash',
    //   price: 2,
    //   new: true,
    //   best: true
    // },
    // {
    //   type: 'text',
    //   driver: 'gemini',
    //   driverDisplayName: 'Gemini',
    //   image: geminiImg,
    //   model: 'gemini-2.5-pro',
    //   name: 'Gemini 2.5 Pro',
    //   fullName: 'Gemini 2.5 Pro',
    //   price: 10,
    //   disabled: isSumoling() && !hasPremiumText() && !hasDriverActiveExternalApiKey('gemini'),
    //   new: true,
    //   best: true
    // },
    // Retired 2026-10-17 and mapped to a successor by legacyModelAliases (and by the API):
    // nano_banana, nano_banana_2, imagen4_preview(_fast), seedream_v4, wan_25_preview,
    // ideogram_v3, gpt_image_2, flux_dev, flux_pro_1.1, flux_pro_kontext, and the OpenAI
    // gpt-image-1-auto (now GPT Image 2.5 on fal). Prices are the API's credits per image,
    // ~300 per dollar of cost. Every fal image runs as a queued job (isJob).
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: googleImg,
      model: 'nano_banana_2_1',
      name: 'Nano Banana 2.1',
      fullName: 'Google Nano Banana 2.1',
      description: 'Professional-grade with superior detail',
      availableSizes: ["16:9", "5:4", "4:3", "3:2", "1:1", "2:3", "3:4", "4:5", "9:16"],
      price: 14,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      best: true,
      allowImageEdit: true,
      defaultImageModel: true,
      proImageModel: true,
      maxInputImages: 10,
      isJob: true,
      creativeStudioModel: true
    },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: googleImg,
      model: 'nano_banana_pro',
      name: 'Nano Banana Pro',
      fullName: 'Google Nano Banana Pro',
      description: 'Professional-grade with superior detail',
      availableSizes: ["16:9", "5:4", "3:2", "1:1", "2:3", "4:5", "9:16"],
      price: 45,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      best: true,
      allowImageEdit: true,
      // proImageModel: true,
      maxInputImages: 20,
      isJob: true
    },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: openaiImg,
      model: 'gpt_image_2_5',
      name: 'GPT Image 2.5',
      fullName: 'OpenAI GPT Image 2.5',
      description: "OpenAI's image model with text & edit",
      availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4"],
      price: 18,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      best: true,
      allowImageEdit: true,
      maxInputImages: 10,
      isJob: true,
    },
    {
      // Sumoling-only unlock for the Creative Studio. Same backend model ID
      // as the entry above; only surfaces for sumolings without premium-image
      // credits or their own Fal key, so it never leaks to /ai-image or any
      // other surface that consumes aiImageModels.
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: googleImg,
      model: 'nano_banana_2_1',
      name: 'Nano Banana 2.1',
      fullName: 'Google Nano Banana 2.1',
      description: 'Professional-grade with superior detail',
      availableSizes: ["16:9", "5:4", "4:3", "3:2", "1:1", "2:3", "3:4", "4:5", "9:16"],
      price: 14,
      hidden: !(isSumoling() && !hasPremiumImages() && !hasDriverActiveExternalApiKey('fal')),
      disabled: !(isSumoling() && !hasPremiumImages() && !hasDriverActiveExternalApiKey('fal')),
      new: true,
      best: true,
      allowImageEdit: true,
      maxInputImages: 10,
      isJob: true,
      sumolingCreativeStudioModel: true,
    },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: falImg,
      model: 'flux_3',
      name: 'FLUX.3',
      fullName: 'Black Forest Labs FLUX.3',
      description: 'Photorealistic detail with accurate prompts',
      needsTranslation: true,
      availableSizes: ["16:9", "5:4", "4:3", "3:2", "1:1", "2:3", "3:4", "4:5", "9:16"],
      price: 15,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      allowImageEdit: true,
      maxInputImages: 10,
      isJob: true,
    },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: falImg,
      model: 'flux_3_klein',
      name: 'FLUX.3 Klein',
      fullName: 'Black Forest Labs FLUX.3 Klein',
      description: 'Fast, low-cost FLUX generation',
      needsTranslation: true,
      availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4"],
      price: 4,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      allowImageEdit: true,
      maxInputImages: 10,
      isJob: true,
    },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: bytedanceImg,
      model: 'seedream_v5_lite',
      name: 'Seedream 5 Lite',
      fullName: 'Seedream 5.0 Lite',
      description: 'High-quality creative image generation',
      needsTranslation: true,
      availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4"],
      price: 11,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      allowImageEdit: true,
      maxInputImages: 10,
      new: true,
      isJob: true,
    },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: ideogramImg,
      model: 'ideogram_v4_5',
      name: 'Ideogram V4.5',
      fullName: 'Ideogram V4.5',
      description: 'Best for legible text in images',
      needsTranslation: true,
      availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4"],
      price: 18,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      allowImageEdit: true,
      maxInputImages: 1,
      new: true,
      isJob: true,
    },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: falImg,
      model: 'qwen_image_3',
      name: 'Qwen Image 3',
      fullName: 'Alibaba Qwen Image 3',
      description: 'Strong text rendering and layouts',
      availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4"],
      price: 12,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      allowImageEdit: true,
      maxInputImages: 4,
      new: true,
      isJob: true,
    },
    {
      type: 'image',
      driver: 'recraft',
      driverDisplayName: 'Recraft',
      image: recraftImg,
      model: 'recraftv3',
      name: 'Recraft v3 - Digital',
      fullName: 'Recraft v3 - Digital',
      needsTranslation: true,
      availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4", "2:3", "3:2"],
      // Repriced 2026-08-26 to match the backend ladder: $0.04 an image at the house rate
      // of ~300 credits per dollar of cost. It was 3, a quarter of that.
      price: 12,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('recraft'),
      new: true,
      canIncludeColors: true,
      colorFormat: "rgb",
      // prompts: []
    },
    // {
    //   type: 'image',
    //   driver: 'ideogram',
    //   driverDisplayName: 'Ideogram',
    //   image: ideogramImg,
    //   model: 'V_2_TURBO',
    //   name: '2.0 Turbo',
    //   fullName: 'Ideogram 2.0 Turbo',
    //   needsTranslation: true,
    //   availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4", "2:3", "3:2"],
    //   price: 2,
    //   disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('ideogram')
    // },
    // {
    //   type: 'image',
    //   driver: 'ideogram',
    //   driverDisplayName: 'Ideogram',
    //   image: ideogramImg,
    //   model: 'V_2',
    //   name: '2.0',
    //   fullName: 'Ideogram 2.0',
    //   needsTranslation: true,
    //   availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4", "2:3", "3:2"],
    //   price: 5,
    //   disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('ideogram')
    // },
    // {
    //   type: 'image',
    //   driver: 'stability',
    //   driverDisplayName: 'Stability',
    //   image: stabilityImg,
    //   model: 'core',
    //   name: 'Core',
    //   fullName: 'Stability Core',
    //   needsTranslation: true,
    //   availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4", "2:3", "3:2"],
    //   price: 2,
    //   disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('stability')
    // },
    // {
    //   type: 'image',
    //   driver: 'stability',
    //   driverDisplayName: 'Stability',
    //   image: stabilityImg,
    //   model: 'ultra',
    //   name: 'Ultra',
    //   fullName: 'Stability Ultra',
    //   needsTranslation: true,
    //   availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4", "2:3", "3:2"],
    //   price: 5,
    //   disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('stability')
    // },
    {
      type: 'image',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: falImg,
      model: 'z_image_turbo',
      name: 'Z-Image Turbo',
      fullName: 'Z-Image Turbo',
      availableSizes: ["1:1", "16:9", "9:16", "4:3", "3:4"],
      price: 2,
      allowImageEdit: true,
      maxInputImages: 1,
      isJob: true,
    },
    // {
    //   type: 'image',
    //   driver: 'openai',
    //   driverDisplayName: 'OpenAI',
    //   image: openaiImg,
    //   model: 'dall-e-3',
    //   name: 'DALL·E 3',
    //   fullName: 'OpenAI DALL·E 3',
    //   availableSizes: ["1:1", "16:9", "9:16"],
    //   price: 1,
    //   disabled: !isSumoling(),
    //   hidden: !isSumoling()
    // },
    // Video Models
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: googleImg,
      model: 'veo_3.1_fast',
      name: 'Veo 3.1 Fast',
      fullName: 'Google Veo 3.1 Fast',
      availableSizes: ["16:9", "9:16"],
      availableDurations: [4, 6, 8],
      generatesWithAudio: true,
      price: 50,
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      best: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: googleImg,
      model: 'veo_3.1',
      name: 'Veo 3.1',
      fullName: 'Google Veo 3.1',
      availableSizes: ["16:9", "9:16"],
      availableDurations: [8],
      generatesWithAudio: true,
      // Repriced 2026-08-26 to match the backend ladder (CreateFalVideo): an 8s clip costs
      // $3.20, the same as Veo 3, so it charges the same 1000 credits. It was 600.
      price: 1000,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      best: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: googleImg,
      model: 'veo_3.1_lite',
      name: 'Veo 3.1 Lite',
      fullName: 'Google Veo 3.1 Lite',
      availableSizes: ["16:9", "9:16"],
      availableDurations: [4, 6, 8],
      generatesWithAudio: true,
      // $0.05/s at 720p with audio.
      price: 15,
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: falImg,
      model: 'kling_3_turbo',
      name: 'Kling 3 Turbo',
      fullName: 'Kling Video 3 Turbo',
      availableSizes: ["16:9", "9:16", "1:1"],
      availableDurations: [5, 10, 15],
      generatesWithAudio: true,
      // $0.112/s at 720p with native audio.
      price: 34,
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      best: true,
      acceptsImageInput: true,
      isJob: true
    },
    // Retired 2026-10-17 and mapped to a successor by legacyModelAliases (and by the API):
    // sora_2 / sora_2_pro (gone from fal), veo_3 / veo_3_fast (deprecated on fal),
    // seedance_1.1_light / _pro and hailuo_02_standard / _premium.
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: wanImg,
      model: 'wan_2.2',
      name: 'Wan 2',
      fullName: 'Wan 2',
      availableSizes: ["16:9", "1:1", "9:16"],
      availableDurations: [5],
      price: 150,
      new: true,
      acceptsImageInput: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: wanImg,
      model: 'wan_3',
      name: 'Wan 3',
      fullName: 'Wan 3.0',
      availableSizes: ["16:9", "1:1", "9:16", "4:3", "3:4"],
      availableDurations: [5, 10, 15],
      generatesWithAudio: true,
      // $0.10/s at 720p.
      price: 30,
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: hailuoImg,
      model: 'h3_max_turbo',
      name: 'MiniMax H3 Max Turbo',
      fullName: 'MiniMax H3 Max Turbo',
      availableSizes: ["16:9", "1:1", "9:16", "21:9", "4:3", "3:4"],
      availableDurations: [5, 6, 8, 10],
      // $0.04/s at 768p.
      price: 12,
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: seedanceImg,
      model: 'seedance_2.0',
      name: 'SeeDance 2.0',
      fullName: 'SeeDance 2.0',
      availableSizes: ["16:9", "1:1", "9:16", "21:9", "4:3", "3:4"],
      availableDurations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      price: 175,
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      best: true,
      acceptsImageInput: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: seedanceImg,
      model: 'seedance_2.0_fast',
      name: 'SeeDance 2.0 Fast',
      fullName: 'SeeDance 2.0 Fast',
      availableSizes: ["16:9", "1:1", "9:16", "21:9", "4:3", "3:4"],
      availableDurations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
      price: 100,
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      isJob: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: veedImg,
      model: 'veed_fabric_1.0',
      name: 'Veed Fabric 1.0',
      fullName: 'Veed Fabric 1.0',
      availableSizes: ["16:9", "1:1", "9:16", "4:3", "3:4"],
      price: 25, // per second
      isPricePerSecond: true,
      requireAudioInput: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      isJob: true,
      hidden: true,
      isAvatarModel: true
    },
    {
      type: 'video',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: veedImg,
      model: 'veed_fabric_1.0_fast',
      name: 'Veed Fabric 1.0 Fast',
      fullName: 'Veed Fabric 1.0 Fast',
      availableSizes: ["16:9", "1:1", "9:16", "4:3", "3:4"],
      // The backend retired this tier on 2026-08-26 and serves veed_fabric_1.0 instead
      // (fal billed fabric-1.0/fast at $0.20/s against $0.08/s for the standard tier).
      // The option is still accepted so this entry keeps working; it now costs the
      // standard tier's 25 credits/s rather than 30.
      price: 25, // per second
      isPricePerSecond: true,
      requireAudioInput: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      acceptsImageInput: true,
      isJob: true,
      hidden: true,
      isAvatarModel: true
    },
    {
      type: 'video',
      driver: 'creatomate',
      driverDisplayName: 'Creatomate',
      image: creatomateImg,
      model: 'creatomate_video',
      name: 'Creatomate',
      fullName: 'Creatomate',
      availableSizes: ["16:9", "1:1", "9:16", "4:3", "3:4"],
      price: 0,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('creatomate'),
      new: true,
      isJob: true,
      hidden: true,
      isAvatarModel: true
    },
    {
      type: 'video',
      driver: 'creatomate',
      driverDisplayName: 'Creatomate',
      image: creatomateImg,
      model: 'creatomate_short',
      name: 'Creatomate Short',
      fullName: 'Creatomate Short',
      availableSizes: ["16:9", "1:1", "9:16", "4:3", "3:4"],
      price: 0, 
      isPricePerSecond: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('creatomate'),
      new: true,
      isJob: true,
      hidden: true,
      isViralShortModel: true
    },
    // Audio Models
    {
      type: 'audio',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: elevenLabsImg,
      model: 'elevenlabs_tts_3',
      name: 'ElevenLabs TTS V3',
      fullName: 'ElevenLabs TTS V3',
      price: 0.04,
      isPricePerInputCharacter: true,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      isJob: true
    },
    {
      type: 'audio',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: elevenLabsImg,
      model: 'elevenlabs_tts_turbo_2.5',
      name: 'ElevenLabs TTS Turbo',
      fullName: 'ElevenLabs TTS Turbo 2.5',
      price: 4,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      isJob: true
    },
    {
      type: 'audio',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: elevenLabsImg,
      model: 'elevenlabs_tts_multilingual_v2',
      name: 'ElevenLabs TTS Multilingual',
      fullName: 'ElevenLabs TTS Multilingual V2',
      price: 4,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      best: true,
      isJob: true
    },
    {
      type: 'audio',
      driver: 'fal',
      driverDisplayName: 'Fal',
      image: beatovenImg,
      model: 'beatoven_music',
      name: 'Beatoven',
      fullName: 'Beatoven Music',
      price: 20,
      disabled: !hasPremiumImages() && isSumoling() && !hasDriverActiveExternalApiKey('fal'),
      new: true,
      isJob: true
    },
  ];

  const availableAiModels = aiModels.filter(e => !e?.disabled);
  const aiTextModels = aiModels.filter(e => e.type == "text");
  const aiImageModels = aiModels.filter(e => e.type == "image");
  const aiVideoModels = aiModels.filter(e => e.type == "video");
  const aiAudioModels = aiModels.filter(e => e.type == "audio");
  const shouldUseSumolingCreativeStudioModel =
    isSumoling() && !hasPremiumImages() && !hasDriverActiveExternalApiKey('fal');
  const creativeStudioImageModel = (
    shouldUseSumolingCreativeStudioModel
      ? aiImageModels.find(m => m.sumolingCreativeStudioModel)
      : aiImageModels.find(m => m.creativeStudioModel)
  )?.model || 'nano_banana_2_1';
  
  const systemMessage = !company?.id ? `` : `
${company?.name || `COMPANY: `}
${company?.description} 
${company?.audience_types && company?.audience_types?.length > 0 ? `Target audiences are: ${company?.audience_types.join(", ")}.` : ``} 
${company?.audience_genders && company?.audience_genders?.length > 0 ? `Target audience gender is: ${company?.audience_genders.join(", ")}.` : ``} 
${company?.audience_ages && company?.audience_ages?.length > 0 ? `Target audience age is: ${company?.audience_ages.join(", ")}.` : ``} 
${company?.website ? `Website is: ${company?.website}.` : ``}
${company?.interests && company?.interests?.length > 0 ? `Interests are: ${company?.interests.join(", ")}.` : ``} 
${company?.tones && company?.tones?.length > 0 ? `Tones are: ${company?.tones.join(", ")}.` : ``} 
${company?.emotions && company?.emotions?.length > 0 ? `Emotions are: ${company?.emotions.join(", ")}.` : ``} 
${company?.syntaxes && company?.syntaxes?.length > 0 ? `Syntaxes are: ${company?.syntaxes.join(", ")}.` : ``} 
${company?.language_types && company?.language_types?.length > 0 ? `Language types are: ${company?.language_types.join(", ")}.` : ``} 
${company?.characters && company?.characters?.length > 0 ? `Characters are: ${company?.characters.join(", ")}.` : ``}
${company?.palettes && company?.palettes?.length > 0 ? `Company colors are: ${company?.palettes.join(", ")}.` : ``}

--------------------------------------

You are the best copywriter of the world. You represent ${company?.name || `COMPANY`}. Your goal is to reach the target audience using the language, tone, emotion, syntax, character and language type you have been provided.

Unless a task explicitly specifies another language, write in ${company?.language || `language ISO code: ${locale}`}.

Do not use markdown notation.
When inserting URLs, insert them without brackets or placeholders, just write the URL.

Today is ${new Date().toUTCString()}.
  `;
  
  const getDriverModel = (modelType) => {
    let defaultModel;
    if (modelType === "text") {
      defaultModel = process.env.REACT_APP_DEFAULT_TEXT_AI_MODEL;
    } else if (modelType === "image") {
      defaultModel = process.env.REACT_APP_DEFAULT_IMAGE_AI_MODEL;
    } else if (modelType === "video") {
      defaultModel = process.env.REACT_APP_DEFAULT_VIDEO_AI_MODEL;
    } else if (modelType === "audio") {
      defaultModel = process.env.REACT_APP_DEFAULT_AUDIO_AI_MODEL;
    }

    let model = company?.ai_preferences?.[`${modelType}_model`] || defaultModel || undefined;

    model = legacyModelAliases[model] || model;

    let driver = availableAiModels.filter(e => e.type == modelType).find(e => e.model == model)?.driver || undefined;
    if (!availableAiModels.filter(e => e.type == modelType).find(e => e.model == model)) {
      model = availableAiModels.filter(e => e.type == modelType)?.[0]?.model;
      driver = availableAiModels.filter(e => e.type == modelType)?.[0]?.driver;
    }

    let size;
    if (modelType === "image") {
      size = company?.ai_preferences?.image_aspect_ratio || "1:1";
    } else if (modelType === "video") {
      size = company?.ai_preferences?.video_aspect_ratio || "16:9";
    } else {
      size = undefined;
    }

    return { model, driver, size }
  }
  
  // Chargeable text generation needs the company onboarding. Returns true when it is done;
  // otherwise it explains why and opens the onboarding.
  const ensureOnboarded = () => {
    if (companyOnboardingCompleted) return true;
    toast.push(
      <Notification
        width={400}
        closable={true}
        duration={10000}
        title={`Onboarding required`}
        type="info"
      >
        {whitelabel?.name} needs more information about your company to generate more accurate and personalized content. Please complete the onboarding process.
      </Notification>
    , {
      placement: "top-center",
    });
    openCompanyOnboarding();
    return false;
  }

  const getByQueryChat = async (query = "", isChargable = true, forceNewQuerySystem = false, newQuerySystem = "", aiResultId = undefined, config = {}, groupId = undefined, _model = undefined, jsonSchema = undefined) => {
  
    if (isChargable && !ensureOnboarded()) {
      return;
    }

    const q_system = forceNewQuerySystem
      ? `
        You are a brand-voice assistant operating under editorial calibration profile. Maintain consistent persona tone across generations.

        Never use em dashes (the "—" character) anywhere in your output. Use a comma, parentheses, or a regular hyphen (-) instead, or rephrase the sentence.

        ${newQuerySystem}
      `
      : `
        You are a brand-voice assistant operating under editorial calibration profile. Maintain consistent persona tone across generations.

        Never use em dashes (the "—" character) anywhere in your output. Use a comma, parentheses, or a regular hyphen (-) instead, or rephrase the sentence.

        ${systemMessage}

        ${newQuerySystem}
      `;

    const { driver: driver, model: model } = aiModels.find(e => e.model == (_model)) || getDriverModel("text");

    const showGenerationError = (err) => toast.push(
      <Notification 
        width={400} 
        closable={true} 
        duration={10000} 
        title={
          <div className="flex flex-col gap-2">
            <b>AI Text Generation Error:</b>
            <p><i>{err?.message || err?.msg || err}</i></p>
            <p>Word credits have not been deducted from your account.</p>
          </div>
        }
        type="danger" 
      />
    );

    const response = await Api.post(`/aiResults/chat`, {}, {
      q: query,
      q_system: q_system,
      company_id: company ? company?.id : undefined,
      chargeable: isChargable ? 1 : 0,
      group_id: groupId ? groupId : undefined,
      driver: isChargable || !!_model ? driver : undefined,
      model: isChargable || !!_model ? model : undefined,
      json_schema: jsonSchema ? JSON.stringify(jsonSchema) : undefined,
      response_format: jsonSchema ? "json_schema" : undefined,
      // Queue the generation and poll for it, so slow models and long texts are not cut
      // off by the request timeout. The API answers with the pending row.
      async: 1,
      ...config
    }).catch((err) => {
      isChargable && showGenerationError(err);
    })

    // A failed request resolves to undefined (the .catch above swallows it and
    // surfaces a toast), so guard before reading response.data.
    let result = response?.data;
    if (result?.id && ["pending", "processing"].includes(result?.status)) {
      result = await pollAiResult(result.id).catch((err) => {
        isChargable && showGenerationError(err);
        return undefined;
      });
    }

    const raw = (result?.response ?? "").trim();

    if (isChargable) {
      updateBalance();
    }

    // Structured-output path: the backend returns a JSON envelope string
    // (e.g. {"result":"..."} or {"items":[...]}). Parse it and return the
    // object so callers can read typed fields. Tolerate stray ```json fences.
    if (jsonSchema) {
      try {
        const unfenced = raw
          .replace(/^```(?:json)?\s*/i, "")
          .replace(/\s*```$/i, "")
          .trim();
        return JSON.parse(unfenced);
      } catch (e) {
        // Fallback: wrap the cleaned text under the schema's first property so
        // callers reading `.result` / `.items` still get a sane shape.
        const cleaned = cleanAIText(stripMarkdown(raw));
        const props = jsonSchema?.schema?.properties || jsonSchema?.properties;
        const firstKey = props ? Object.keys(props)[0] : "result";
        return { [firstKey]: cleaned };
      }
    }

    // Legacy string path (no schema): clean preambles / quotes / markdown.
    return cleanAIText(stripMarkdown(raw));
  }

  // Copy tasks (post, title, thread). The API builds the prompts and cleans the output, so only
  // structured fields go out. Returns the parsed JSON ({posts}, {result} or {items}), or
  // undefined when the request fails. The onboarding gate is the caller's job (ensureOnboarded),
  // so a batch of parallel requests checks it once. `silent` skips the error toast; `onError`
  // still receives the error, so the caller can show one toast for the batch.
  const getCopy = async (task, payload = {}, { chargeable = true, silent = false, onError = undefined } = {}) => {
    const { driver, model } = chargeable ? getDriverModel("text") : {};
    let failed = false;

    const fail = (err) => {
      failed = true;
      onError?.(err);
      if (!silent) showTextGenerationError(err, chargeable);
    };

    const response = await Api.post(`/aiResults/copy/${task}`, {}, {
      ...payload,
      company_id: company?.id,
      ...(chargeable ? { driver, model } : {}),
      // Queued like chat (see getByQueryChat): the API answers with the pending row to poll.
      async: 1,
    }).catch(fail);

    let result = response?.data;
    if (!failed && result?.id && ["pending", "processing"].includes(result?.status)) {
      result = await pollAiResult(result.id).catch((err) => {
        fail(err);
        return undefined;
      });
    }

    if (failed) return undefined;

    if (chargeable) {
      updateBalance();
    }

    try {
      return parseJSONResponse((result?.response ?? "").trim());
    } catch (e) {
      return undefined;
    }
  }
  
  const getImages = async (query = "", aspect_ratio = "1:1", _model = undefined, addColors = false, imageUrls = [], queue = undefined) => {
    
    query = query.trim();

    const { driver: defaultDriver, model: defaultModel } = getDriverModel("image");
    const modelObj = availableAiModels.find(e => e.model == (_model || defaultModel) && e.type == 'image');

    if (!modelObj?.availableSizes?.includes(aspect_ratio)) {  
      aspect_ratio = aiSizeOptions.filter(e => modelObj?.availableSizes?.includes(e.id))?.[0]?.id || "1:1";
    }
    
    let colors = addColors && modelObj?.canIncludeColors
      ? modelObj?.colorFormat == "rgb"
        ? company?.palettes?.map(hex => {
            const r = parseInt(hex.slice(1,3), 16);
            const g = parseInt(hex.slice(3,5), 16);
            const b = parseInt(hex.slice(5,7), 16);
            return {r, g, b};
          }).filter(color => 
            (!isNaN(color.r) && color.r !== null) &&
            (!isNaN(color.g) && color.g !== null) &&
            (!isNaN(color.b) && color.b !== null)
          )
        : undefined // TODO: Add HEX colors
      : undefined

    let finalModel = modelObj?.model || defaultModel;
    let finalDriver = modelObj?.driver || defaultDriver;
    let finalQuality = modelObj?.quality || undefined;

    // If imageUrls are provided and the model does not support image editing, 
    // use the first image url as the base image
    if (imageUrls?.length > 0) {
      if (!modelObj?.allowImageEdit) {
        const newModelObj = aiModels.find(e => e.type == "image" && !!e?.allowImageEdit && !e?.disabled);
        if (newModelObj) {
          finalModel = newModelObj?.model;
          finalDriver = newModelObj?.driver;
          finalQuality = newModelObj?.quality;
        } else {
          toast.push(
            <Notification 
              width={400} 
              closable={true} 
              duration={10000} 
              title={`AI Image Generation Error`}
              type="danger" 
            >
              The selected model does not support image editing. Please select a different model.
            </Notification>
          );
        }
      }
    }

    // Remove quality from model if it exists
    if (finalQuality) {
      finalModel = finalModel.replace(`-${finalQuality}`, "");
    }

    if (queue === undefined) {
      queue = finalDriver === 'fal' || !!aiModels.find(e => e.type == 'image' && e.model == finalModel)?.isJob;
    }
    
    const response = await Api.post(`/aiResults/image`, {}, {
      q: query,
      company_id: company ? company?.id : undefined,
      n: 1,
      chargeable: 1,
      aspect_ratio: aspect_ratio,
      driver: finalDriver,
      model: finalModel,
      colors: colors,
      quality: finalQuality,
      image_urls: imageUrls && imageUrls?.length > 0 ? imageUrls : undefined,
      image_url: imageUrls?.length == 1 ? imageUrls[0] : undefined,
      queue: queue
    }).catch((err) => {
      toast.push(
        <Notification 
          width={400} 
          closable={true} 
          duration={10000} 
          title={
            <div className="flex flex-col gap-2">
              <b>AI Image Generation Error:</b>
              <p><i>{err?.message || err?.msg || err}</i></p>
              <p>Image credits have not been deducted from your account.</p>
            </div>
          }
          type="danger" 
        />
      );
      throw err;
    });

    // Check if response has status field
    if (response.data.status) {
      const { status, id } = response.data;
      
      // If status is pending, start polling or return pending response based on queue parameter
      if (status === 'pending') {
        if (queue) {
          const pollForCompletion = async () => {
            let attempts = 0;
            const maxAttempts = 60; // 5 minutes max (60 * 5 seconds)
            
            while (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
              attempts++;
              
              try {
                const pollResponse = await Api.get(`/aiResults/${id}`);
                const { status: currentStatus, response: imageResponse } = pollResponse.data;
                
                if (currentStatus === 'completed') {
                  updateBalance();
                  return {
                    id: id,
                    urls: imageResponse.trim().split(","),
                    status: 'completed'
                  };
                } else if (currentStatus === 'failed') {
                  toast.push(
                    <Notification 
                      width={400} 
                      closable={true} 
                      duration={10000} 
                      title={
                        <div className="flex flex-col gap-2">
                          <b>AI Image Generation Failed:</b>
                          <p><i>The image generation process failed. Please try again.</i></p>
                          <p>Image credits have not been deducted from your account.</p>
                        </div>
                      }
                      type="danger" 
                    />
                  );
                  throw new Error('Image generation failed');
                }
                // If still pending, continue polling
              } catch (err) {
                toast.push(
                  <Notification 
                    width={400} 
                    closable={true} 
                    duration={10000} 
                    title={
                      <div className="flex flex-col gap-2">
                        <b>AI Image Generation Error:</b>
                        <p><i>{err?.message || err?.msg || 'Error checking image generation status'}</i></p>
                        <p>Image credits have not been deducted from your account.</p>
                      </div>
                    }
                    type="danger" 
                  />
                );
                throw err;
              }
            }
            
            // If we've exceeded max attempts
            toast.push(
              <Notification 
                width={400} 
                closable={true} 
                duration={10000} 
                title={
                  <div className="flex flex-col gap-2">
                    <b>AI Image Generation Timeout:</b>
                    <p><i>Image generation is taking longer than expected. Please try again later.</i></p>
                    <p>Image credits have not been deducted from your account.</p>
                  </div>
                }
                type="warning" 
              />
            );
            throw new Error('Image generation timeout');
          };
          
          return await pollForCompletion();
        } else {
          return {
            id: id,
            status: 'pending',
            message: 'Image generation started. This may take a few moments.'
          };
        }
      } else if (status === 'failed') {
        toast.push(
          <Notification 
            width={400} 
            closable={true} 
            duration={10000} 
            title={
              <div className="flex flex-col gap-2">
                <b>AI Image Generation Failed:</b>
                <p><i>The image generation process failed. Please try again.</i></p>
                <p>Image credits have not been deducted from your account.</p>
              </div>
            }
            type="danger" 
          />
        );
        throw new Error('Image generation failed');
      } else if (status === 'completed') {
        // If already completed, proceed normally
        updateBalance();
        return {
          id: response.data.id,
          urls: response.data.response.trim().split(","),
          status: 'completed'
        };
      }
    }
    
    // Fallback for responses without status field (backward compatibility)
    updateBalance();
    
    return {
      id: response.data.id,
      urls: response.data.response.trim().split(","),
      status: 'completed'
    }
  }

  const getVideos = async (query = "", aspect_ratio = "16:9", _model = undefined, imageUrl = undefined, enablePolling = true, audioUrl = undefined, duration = undefined, render_script = undefined) => {
    
    query = query.trim();

    const { driver: defaultDriver, model: defaultModel } = getDriverModel("video") || { driver: 'fal', model: aiVideoModels?.[0]?.model };
    const modelObj = aiVideoModels?.find(e => e.model == (_model || defaultModel)) || aiVideoModels?.[0];

    const { driver, model } = modelObj || { driver: 'fal', model: defaultModel };

    if (!modelObj?.availableSizes?.includes(aspect_ratio)) {  
      aspect_ratio = modelObj?.availableSizes?.[0] || "16:9";
    }

    if (!modelObj?.availableDurations?.includes(duration)) {  
      duration = modelObj?.availableDurations?.[0] || undefined;
    }

    let finalModel = modelObj?.model || defaultModel;
    let finalDriver = modelObj?.driver || defaultDriver || 'fal';
    
    const response = await Api.post(`/aiResults/video`, {}, {
      q: query,
      company_id: company ? company?.id : undefined,
      chargeable: 1,
      aspect_ratio: aspect_ratio,
      driver: finalDriver,
      model: finalModel,
      image_url: imageUrl || undefined,
      audio_url: audioUrl || undefined,
      duration: duration || undefined,
      render_script: render_script || undefined,
      type: 'video'
    }).catch((err) => {
      toast.push(
        <Notification 
          width={400} 
          closable={true} 
          duration={10000} 
          title={
            <div className="flex flex-col gap-2">
              <b>AI Video Generation Error:</b>
              <p><i>{err?.message || err?.msg || err}</i></p>
              <p>Video credits have not been deducted from your account.</p>
            </div>
          }
          type="danger" 
        />
      );
      throw err;
    });

    // Check if response has status field
    if (response.data.status) {
      const { status, id } = response.data;
      
      // If status is pending, start polling or return pending response based on enablePolling
      if (status === 'pending') {
        if (enablePolling) {
          const pollForCompletion = async () => {
            let attempts = 0;
            const maxAttempts = 60; // 10 minutes max (60 * 10 seconds)
            
            while (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
              attempts++;
              
              try {
                const pollResponse = await Api.get(`/aiResults/${id}`);
                const { status: currentStatus, response: videoResponse } = pollResponse.data;
                
                if (currentStatus === 'completed') {
                  updateBalance();
                  return {
                    id: id,
                    urls: videoResponse.trim().split(","),
                    status: 'completed'
                  };
                } else if (currentStatus === 'failed') {
                  toast.push(
                    <Notification 
                      width={400} 
                      closable={true} 
                      duration={10000} 
                      title={
                        <div className="flex flex-col gap-2">
                          <b>AI Video Generation Failed:</b>
                          <p><i>The video generation process failed. Please try again.</i></p>
                          <p>Video credits have not been deducted from your account.</p>
                        </div>
                      }
                      type="danger" 
                    />
                  );
                  throw new Error('Video generation failed');
                }
                // If still pending, continue polling
              } catch (err) {
                toast.push(
                  <Notification
                    width={400}
                    closable={true}
                    duration={10000}
                    title={
                      <div className="flex flex-col gap-2">
                        <b>AI Video Generation Error:</b>
                        <p><i>Error checking video status: {err?.message || err?.msg || err}</i></p>
                      </div>
                    }
                    type="danger"
                  />
                );
                throw err;
              }
            }
            
            // If we've reached max attempts, throw timeout error
            toast.push(
              <Notification
                width={400}
                closable={true}
                duration={10000}
                title={
                  <div className="flex flex-col gap-2">
                    <b>AI Video Generation Timeout:</b>
                    <p><i>Video generation is taking longer than expected. Please check your history later.</i></p>
                  </div>
                }
                type="warning"
              />
            );
            throw new Error('Video generation timeout');
          };
          
          return await pollForCompletion();
        } else {
          return {
            id: id,
            status: 'pending',
            message: 'Video generation started. This may take 2-5 minutes.'
          };
        }
      } else if (status === 'failed') {
        toast.push(
          <Notification 
            width={400} 
            closable={true} 
            duration={10000} 
            title={
              <div className="flex flex-col gap-2">
                <b>AI Video Generation Failed:</b>
                <p><i>The video generation process failed. Please try again.</i></p>
                <p>Video credits have not been deducted from your account.</p>
              </div>
            }
            type="danger" 
          />
        );
        throw new Error('Video generation failed');
      } else if (status === 'completed') {
        // If already completed, proceed normally
        updateBalance();
        return {
          id: response.data.id,
          urls: response.data.response.trim().split(","),
          status: 'completed'
        };
      }
    }
    
    // Fallback for responses without status field (backward compatibility)
    updateBalance();
    
    return {
      id: response.data.id,
      urls: response.data.response.trim().split(","),
      status: 'completed'
    }
  }
  
  const getAudio = async (text = "", voice = "Rachel", _model = undefined, config = {}, queue = undefined) => {
    
    text = text.trim();

    const { driver: defaultDriver, model: defaultModel } = getDriverModel("audio") || { driver: 'fal', model: aiAudioModels?.[0]?.model };
    const modelObj = aiAudioModels?.find(e => e.model == (_model || defaultModel)) || aiAudioModels?.[0];

    const { driver, model } = modelObj || { driver: 'fal', model: defaultModel };

    let finalModel = modelObj?.model || defaultModel;
    let finalDriver = modelObj?.driver || defaultDriver || 'fal';
    
    const response = await Api.post(`/aiResults/audio`, {}, {
      q: text,
      company_id: company ? company?.id : undefined,
      chargeable: 1,
      driver: finalDriver,
      model: finalModel,
      voice: voice || 'Rachel',
      type: 'audio',
      // The model entry says whether it runs as a queued job (finalModel is just its id).
      queue: queue === undefined ? !!modelObj?.isJob : queue,
      ...config
    }).catch((err) => {
      toast.push(
        <Notification 
          width={400} 
          closable={true} 
          duration={10000} 
          title={
            <div className="flex flex-col gap-2">
              <b>AI Audio Generation Error:</b>
              <p><i>{err?.message || err?.msg || err}</i></p>
              <p>Audio credits have not been deducted from your account.</p>
            </div>
          }
          type="danger" 
        />
      );
      throw err;
    });

    // Check if response has status field (for async processing)
    if (response.data.status) {
      const { status, id } = response.data;
      
      // If status is pending, start polling or return pending response based on queue parameter
      if (status === 'pending') {
        if (queue) {
          const pollForCompletion = async () => {
            let attempts = 0;
            const maxAttempts = 60; // 5 minutes max (60 * 5 seconds)
            
            while (attempts < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
              attempts++;
              
              try {
                const pollResponse = await Api.get(`/aiResults/${id}`);
                const { status: currentStatus, response: audioResponse } = pollResponse.data;
                
                if (currentStatus === 'completed') {
                  updateBalance();
                  return {
                    id: id,
                    url: audioResponse.trim(),
                    status: 'completed'
                  };
                } else if (currentStatus === 'failed') {
                  toast.push(
                    <Notification 
                      width={400} 
                      closable={true} 
                      duration={10000} 
                      title={
                        <div className="flex flex-col gap-2">
                          <b>AI Audio Generation Failed:</b>
                          <p><i>The audio generation process failed. Please try again.</i></p>
                          <p>Audio credits have not been deducted from your account.</p>
                        </div>
                      }
                      type="danger" 
                    />
                  );
                  throw new Error('Audio generation failed');
                }
                // If still pending, continue polling
              } catch (err) {
                toast.push(
                  <Notification
                    width={400}
                    closable={true}
                    duration={10000}
                    title={
                      <div className="flex flex-col gap-2">
                        <b>AI Audio Generation Error:</b>
                        <p><i>{err?.message || err?.msg || 'Error checking audio generation status'}</i></p>
                        <p>Audio credits have not been deducted from your account.</p>
                      </div>
                    }
                    type="danger"
                  />
                );
                throw err;
              }
            }
            
            // If we've exceeded max attempts
            toast.push(
              <Notification
                width={400}
                closable={true}
                duration={10000}
                title={
                  <div className="flex flex-col gap-2">
                    <b>AI Audio Generation Timeout:</b>
                    <p><i>Audio generation is taking longer than expected. Please try again later.</i></p>
                    <p>Audio credits have not been deducted from your account.</p>
                  </div>
                }
                type="warning"
              />
            );
            throw new Error('Audio generation timeout');
          };
          
          return await pollForCompletion();
        } else {
          return {
            id: id,
            status: 'pending',
            message: 'Audio generation started. This may take a few moments.'
          };
        }
      } else if (status === 'failed') {
        toast.push(
          <Notification 
            width={400} 
            closable={true} 
            duration={10000} 
            title={
              <div className="flex flex-col gap-2">
                <b>AI Audio Generation Failed:</b>
                <p><i>The audio generation process failed. Please try again.</i></p>
                <p>Audio credits have not been deducted from your account.</p>
              </div>
            }
            type="danger" 
          />
        );
        throw new Error('Audio generation failed');
      } else if (status === 'completed') {
        // If already completed, proceed normally
        updateBalance();
        return {
          id: response.data.id,
          url: response.data.response.trim(),
          status: 'completed'
        };
      }
    }
    
    // Fallback for responses without status field (backward compatibility)
    updateBalance();
    
    return {
      id: response.data.id,
      url: response.data.response.trim(),
      status: 'completed'
    }
  }

  const getLastHistoryAIImageURL = async (id = null) => {
    const results = await Api.get(`/aiResults?include=images,images.thumbnail,user,user.image&sort=-created_at&filter[company_id]=${company?.id}&filter[type]=image`).then(({ data: results }) => results);
    const result = id 
    ? results?.find(r => r?.id == id)
    : results?.[0];
    return result?.images?.find(i => i?.status == "analyzed")?.url || result?.response?.split(",")?.[0] || null
  }
  
  return (
    <AIContext.Provider value={{ 
      getByQueryChat, 
      getCopy,
      ensureOnboarded,
      getDriverModel,
      getImages,
      getVideos,
      getAudio,
      getLastHistoryAIImageURL,
      aiModels,
      availableAiModels,
      aiTextModels,
      aiImageModels,
      aiVideoModels,
      aiAudioModels,
      creativeStudioImageModel,
      systemMessage
    }}>
      {children}
    </AIContext.Provider>
  );
};

export { AIContext, AIProvider, showTextGenerationError };